import type {
	CoursesAdminReorderCourseModulesDto,
	CoursesAdminUpsertCourseDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import type {
	AdminListCoursesOutput,
	AdminUpsertCourseInput,
	AdminUpsertCourseOutput,
	AdminDeleteCourseInput,
	AdminDeleteCourseOutput,
	AdminGetCourseInput,
	AdminGetCourseOutput,
	AdminListCoursesPickerOutput,
	AdminReorderCourseModulesInput,
	AdminReorderCourseModulesOutput,
	CreateCourseBannerUploadUrlInput,
	CreateCourseBannerUploadUrlOutput,
	ConfirmCourseBannerInput,
	ConfirmCourseBannerOutput,
} from '../../types/admin-courses.types';

const TAG = 'courses:admin-courses' as const;
const INVALIDATES = [
	TAG,
	'courses:courses',
	'courses:modules',
	'courses:lessons',
	'courses:dashboard',
] as const;

export const adminCoursesEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminListCourses: builder.query<AdminListCoursesOutput, void>({
			query: () => ({ url: 'courses/admin/courses' }),
			providesTags: [TAG],
		}),
		adminUpsertCourse: builder.mutation<AdminUpsertCourseOutput, AdminUpsertCourseInput>({
			query: (body) => ({
				url: 'courses/admin/courses',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminUpsertCourseDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminDeleteCourse: builder.mutation<AdminDeleteCourseOutput, AdminDeleteCourseInput>({
			query: ({ id }) => ({
				url: `courses/admin/courses/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminGetCourse: builder.query<AdminGetCourseOutput, AdminGetCourseInput>({
			query: ({ courseId }) => ({ url: `courses/admin/courses/${encodeURIComponent(courseId)}` }),
			providesTags: [TAG],
		}),
		adminListCoursesPicker: builder.query<AdminListCoursesPickerOutput, void>({
			query: () => ({ url: 'courses/admin/courses/picker' }),
			providesTags: [TAG],
		}),
		adminReorderCourseModules: builder.mutation<
			AdminReorderCourseModulesOutput,
			AdminReorderCourseModulesInput
		>({
			query: ({ courseId, ...body }) => ({
				url: `courses/admin/courses/${encodeURIComponent(courseId)}/modules/reorder`,
				method: 'POST',
				body: body satisfies Wire<CoursesAdminReorderCourseModulesDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		createCourseBannerUploadUrl: builder.mutation<
			CreateCourseBannerUploadUrlOutput,
			CreateCourseBannerUploadUrlInput
		>({
			query: ({ courseId, ...body }) => ({
				url: `courses/admin/courses/${encodeURIComponent(courseId)}/banner/upload-url`,
				method: 'POST',
				body,
			}),
		}),
		confirmCourseBanner: builder.mutation<ConfirmCourseBannerOutput, ConfirmCourseBannerInput>({
			query: ({ courseId, ...body }) => ({
				url: `courses/admin/courses/${encodeURIComponent(courseId)}/banner/confirm`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useAdminListCoursesQuery,
	useLazyAdminListCoursesQuery,
	useAdminUpsertCourseMutation,
	useAdminDeleteCourseMutation,
	useAdminGetCourseQuery,
	useLazyAdminGetCourseQuery,
	useAdminListCoursesPickerQuery,
	useLazyAdminListCoursesPickerQuery,
	useAdminReorderCourseModulesMutation,
	useCreateCourseBannerUploadUrlMutation,
	useConfirmCourseBannerMutation,
} = adminCoursesEndpoints;
